import React, { useEffect, useState } from "react";
import { crmApi } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { cn } from "../lib/utils";
import {
  TrendingUp,
  DollarSign,
  Building2,
  Calendar,
  Users,
} from "lucide-react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const CrmPnlPage = () => {
  const { formatAmount } = useCurrency();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(2026);

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const fetchData = async () => {
    try {
      const res = await crmApi.getStats(selectedYear);
      setStats(res.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split("-").map(Number);
    return `${MONTHS[month - 1]} ${year}`;
  };

  const totals = stats?.totals || { planned: 0, actual: 0, paid: 0, invoiced: 0 };
  const collectionRate = totals.planned > 0 ? ((totals.paid / totals.planned) * 100).toFixed(1) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="crm-pnl-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">CRM P&L</h1>
          <p className="text-sm text-zinc-500">DevBase revenue analysis</p>
        </div>

        <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
          <SelectTrigger className="w-[120px] bg-zinc-900 border-zinc-800 text-zinc-50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="2025" className="text-zinc-50">2025</SelectItem>
            <SelectItem value="2026" className="text-zinc-50">2026</SelectItem>
            <SelectItem value="2027" className="text-zinc-50">2027</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Annual Plan</span>
              <DollarSign className="h-4 w-4 text-sky-500" />
            </div>
            <p className="text-2xl font-bold text-sky-400">{formatAmount(totals.planned, "USD")}</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Paid</span>
              <TrendingUp className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-400">{formatAmount(totals.paid, "USD")}</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Invoiced</span>
              <Calendar className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-orange-400">{formatAmount(totals.invoiced, "USD")}</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Pending</span>
              <Building2 className="h-4 w-4 text-zinc-500" />
            </div>
            <p className="text-2xl font-bold text-zinc-400">{formatAmount(totals.planned - totals.paid - totals.invoiced, "USD")}</p>
          </CardContent>
        </Card>

        <Card className={cn(
          "border-zinc-800",
          parseFloat(collectionRate) >= 80 ? "bg-green-500/10" : 
          parseFloat(collectionRate) >= 50 ? "bg-orange-500/10" : "bg-zinc-900/50"
        )}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Collection</span>
              <Users className="h-4 w-4 text-purple-500" />
            </div>
            <p className={cn(
              "text-2xl font-bold",
              parseFloat(collectionRate) >= 80 ? "text-green-400" : 
              parseFloat(collectionRate) >= 50 ? "text-orange-400" : "text-zinc-400"
            )}>{collectionRate}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Breakdown Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
            <Calendar className="h-4 w-4 text-green-500" />
            {selectedYear} Monthly Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {!stats?.monthly_breakdown?.length ? (
            <div className="text-center py-12 text-zinc-500">
              <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No data for {selectedYear}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400 font-medium">Month</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Plan</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Invoiced</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Paid</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Delta</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-center">Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.monthly_breakdown.map((row) => {
                  const delta = (row.paid || 0) - (row.planned || 0);
                  const rate = row.planned > 0 ? ((row.paid / row.planned) * 100).toFixed(0) : 0;
                  
                  return (
                    <TableRow key={row.month} className="border-zinc-800 hover:bg-zinc-800/50">
                      <TableCell>
                        <span className="text-zinc-50 font-medium">{formatMonth(row.month)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono text-zinc-400">{formatAmount(row.planned || 0, "USD")}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono text-orange-400">{formatAmount(row.invoiced || 0, "USD")}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono text-green-400">{formatAmount(row.paid || 0, "USD")}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={cn(
                          "font-mono",
                          delta >= 0 ? "text-green-400" : "text-red-400"
                        )}>
                          {delta >= 0 ? "+" : ""}{formatAmount(Math.abs(delta), "USD")}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {parseFloat(rate) >= 100 ? (
                          <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20">{rate}%</Badge>
                        ) : parseFloat(rate) >= 80 ? (
                          <Badge variant="outline" className="bg-sky-500/10 text-sky-400 border-sky-500/20">{rate}%</Badge>
                        ) : parseFloat(rate) > 0 ? (
                          <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/20">{rate}%</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-zinc-800 text-zinc-500 border-zinc-700">0%</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* TOTAL Row */}
                <TableRow className="border-zinc-800 bg-zinc-800/30 font-bold">
                  <TableCell><span className="text-zinc-50 font-bold">TOTAL</span></TableCell>
                  <TableCell className="text-right">
                    <span className="font-mono text-zinc-300 font-bold">${totals.planned.toLocaleString()}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-mono text-orange-400 font-bold">${totals.invoiced.toLocaleString()}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-mono text-green-400 font-bold">${totals.paid.toLocaleString()}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={cn(
                      "font-mono font-bold",
                      (totals.paid - totals.planned) >= 0 ? "text-green-400" : "text-red-400"
                    )}>
                      {(totals.paid - totals.planned) >= 0 ? "+" : ""}{(totals.paid - totals.planned).toLocaleString()}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className={cn(
                      parseFloat(collectionRate) >= 80 ? "bg-green-500/10 text-green-400 border-green-500/20" :
                      parseFloat(collectionRate) >= 50 ? "bg-orange-500/10 text-orange-400 border-orange-500/20" :
                      "bg-zinc-800 text-zinc-500 border-zinc-700"
                    )}>{collectionRate}%</Badge>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CrmPnlPage;
